import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { Ablageort, ApiClient, Dateityp, Ordner, VorgangsBezug } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { Dateiwahl, KiKnopf } from "../meHuellen.js";
import { apiMeldung } from "../vorgangsdetail.js";
import { FolderTreeSelect } from "./FolderTreeSelect.js";
import { folderPath } from "./folderTree.js";
import { isTooLarge, sendInPackets, tooLargeResult } from "./upload.js";
import type { UploadRow } from "./upload.js";
import { StorageLine, UploadResultList } from "./UploadResultList.js";
import { DOCUMENT_FILE_TEXT as T } from "./texts.js";
import "./documentFiles.css";

export type UploadFilesClient = Pick<ApiClient,
  "uploadDocumentFiles" | "linkDocumentFile" | "listFolders" | "listFileTypes">;

/**
 * Das Modalpanel „Dateien hochladen“ (Spec „Abschnitt ‚Dateien‘ › Werkzeugleiste“; 469): rechts angeschlagen, Größe nach
 * Inhalt. Mehrfachwahl mit Ziehen-und-Ablegen (`me-eingabe art="datei"`), darunter die Ablagezeile mit dem Ort, den
 * der Server nehmen wird („wird beim Hochladen angelegt“, solange es ihn nicht gibt), und „Anderen Ort wählen“: Der
 * Ordnerbaum des Kunden steht seitlich, die Wahl gilt nur für diesen Upload (sie lebt in diesem Panel). „Hochladen“ ist
 * die Bestätigung.
 *
 * Vor dem Senden: Dateien über 50 MB gehen nicht hinaus und stehen als abgewiesen im Ergebnis; mehr als 20 Dateien
 * gehen in Paketen zu je 20 nacheinander (`DOCUMENT_FILE_LIMITS`, K1). Danach steht je Datei das Ergebnis da, an der
 * Doublette der Knopf „Vorhandene Datei verknüpfen“. `onChanged` lädt den Abschnitt nach jedem Schreibweg neu.
 */
export function UploadFilesPanel(p: {
  client: UploadFilesClient; kundeId: string; bezug: VorgangsBezug; storage: Ablageort;
  onChanged: () => Promise<void>; onClose: () => void;
}): ReactElement {
  const { client, kundeId, bezug } = p;
  const [files, setFiles] = useState<File[]>([]);
  const [fileTypes, setFileTypes] = useState<Dateityp[]>([]);
  const [fileType, setFileType] = useState("");
  /** Der andere Ort für DIESEN Upload; `null` = Ablageordner des Vorgangs. */
  const [place, setPlace] = useState<{ id: string; path: string[] } | null>(null);
  /** Der seitliche Ordnerbaum: offen mit der vorläufigen Wahl. */
  const [placeChoice, setPlaceChoice] = useState<{ folders: Ordner[]; id: string | null } | null>(null);
  /** Je Datei das Ergebnis — ab hier zeigt das Panel nur noch sie. */
  const [results, setResults] = useState<UploadRow[] | null>(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Kategorien sind optional: Scheitert das Laden, bleibt das Panel ohne sie bedienbar.
  useEffect(() => {
    let gone = false;
    client.listFileTypes().then((a) => { if (!gone) setFileTypes(a.items); }).catch(() => {});
    return () => { gone = true; };
  }, [client]);

  const toSend = files.filter((f) => !isTooLarge(f));
  const unsaved = results === null && files.length > 0;
  /* Der Wächter gegen das Schließen des BROWSERS: Kreuz, Escape und Fuß fängt `cancel`, einen geschlossenen Reiter nur
     `beforeunload` (A-K8). */
  useBeforeUnload(unsaved || running);

  async function upload(): Promise<void> {
    if (toSend.length === 0 || running) return;
    setRunning(true);
    setMessage(null);
    const options = { ...(place === null ? {} : { folderId: place.id }), ...(fileType === "" ? {} : { fileTypeId: fileType }) };
    // Das gescheiterte Paket und alle späteren sind nicht abgelegt — auch sie bekommen je Datei ein Ergebnis.
    const sent = await sendInPackets(client, kundeId, bezug, toSend, options);
    if (sent.error !== null) setMessage(apiMeldung(sent.error, T.uploadFailed));
    if (sent.sent > 0) await p.onChanged();
    setRunning(false);
    let next = 0;
    setResults(files.map((file) => ({ file, result: isTooLarge(file) ? tooLargeResult(file) : sent.results[next++] ?? null })));
  }

  async function linkExisting(index: number, fileId: string, name: string): Promise<void> {
    setRunning(true);
    setMessage(null);
    try {
      await client.linkDocumentFile(kundeId, bezug, fileId);
      setResults((old) => (old ?? []).map((r, j) => (j === index ? { ...r, result: { status: "linked", name } } : r)));
      await p.onChanged();
    } catch (e) {
      setMessage(apiMeldung(e, T.linkFailed));
    }
    setRunning(false);
  }

  async function openPlaceChoice(): Promise<void> {
    setMessage(null);
    try {
      setPlaceChoice({ folders: (await client.listFolders(kundeId)).items, id: place?.id ?? null });
    } catch (e) {
      setMessage(apiMeldung(e, T.foldersLoadFailed));
    }
  }

  async function cancel(): Promise<void> {
    if (running || !(await darfAbbrechen(unsaved))) return;
    p.onClose();
  }

  const onLinkExisting = (index: number, fileId: string, name: string): void => { void linkExisting(index, fileId, name); };
  return (
    <Modal titel={T.upload} onAbbrechen={() => { void cancel(); }} abbrechenGesperrt={running}>
      {message !== null && <Hinweis art="fehler">{message}</Hinweis>}
      {results === null ? (
        <div className="doc-files__upload">
          <Dateiwahl id="doc-files-input" beschriftung={T.filesField} mehrere gesperrt={running} onDateien={setFiles} />
          <UploadResultList rows={files.map((file) => ({ file, result: null }))} label={T.chosenFiles} busy={running}
            onLinkExisting={onLinkExisting} />
          <StorageLine path={place?.path ?? p.storage.path} willCreate={place === null && p.storage.willCreate} />
          <div className="doc-files__storage-actions">
            <KiKnopf kompakt deaktiviert={running} onClick={() => { void openPlaceChoice(); }}>{T.otherPlace}</KiKnopf>
            {place !== null && <KiKnopf kompakt deaktiviert={running} onClick={() => setPlace(null)}>{T.defaultPlace}</KiKnopf>}
          </div>
          <Auswahl id="doc-files-category" name="doc-files-category" beschriftung={T.category} wert={fileType} gesperrt={running}
            onGeaendert={(d) => setFileType(d.wert)}>
            <option value="">{T.noCategory}</option>
            {fileTypes.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Auswahl>
        </div>
      ) : (
        <UploadResultList rows={results} label={T.results} busy={running} onLinkExisting={onLinkExisting} />
      )}
      {results === null ? (
        <ModalFuss speichernText={T.uploadConfirm} speichernGesperrt={toSend.length === 0 || running}
          abbrechenGesperrt={running} onSpeichern={() => { void upload(); }} onAbbrechen={() => { void cancel(); }} />
      ) : (
        <ModalFuss nurSchliessen speichernText={T.done} speichernGesperrt={running}
          onSpeichern={p.onClose} onAbbrechen={p.onClose} />
      )}

      {placeChoice !== null && (
        <Modal titel={T.choosePlaceTitle} onAbbrechen={() => setPlaceChoice(null)}>
          {placeChoice.folders.length === 0 && <p className="doc-files__muted">{T.noFolders}</p>}
          <FolderTreeSelect folders={placeChoice.folders} selected={placeChoice.id}
            onSelect={(id) => setPlaceChoice({ ...placeChoice, id })} />
          <ModalFuss speichernText={T.choosePlaceConfirm} speichernGesperrt={placeChoice.id === null}
            onSpeichern={() => {
              if (placeChoice.id !== null) setPlace({ id: placeChoice.id, path: folderPath(placeChoice.folders, placeChoice.id) });
              setPlaceChoice(null);
            }}
            onAbbrechen={() => setPlaceChoice(null)} />
        </Modal>
      )}
    </Modal>
  );
}
